/**
 * Leave plans — who gets which leave rules, from when (ADR 0032).
 *
 * WHAT IS DECIDED HERE and every implementation must agree with: the vocabulary of a plan, how a
 * plan is aimed at people (assignments and their precedence), and what a plan can override on a
 * leave type (rules). The resolver that applies it lives in `apps/api`; nothing here reads a
 * database.
 *
 * THE FALLBACK IS THE WHOLE COMPATIBILITY STORY. Today every rule sits on `LeaveType` and applies
 * to everyone. With no plan, no matching assignment, or no rule for the leave type in the matching
 * plan, the leave type's own values apply EXACTLY as before. A rule field that is `null` means
 * "inherit the leave type's value", never "none" — so an empty plan changes nothing.
 *
 * EFFECTIVE-DATED (invariant 5). Rules and assignments carry `validFrom` / `validTo`. A change
 * closes the open row the day before and opens a new one; it never edits a row that has history,
 * so "what applied to Priya's Earned Leave on 15 March" is a query, and editing a plan next month
 * cannot rewrite last month.
 *
 * PRECEDENCE (one assignment wins per person, per date):
 *   1. most specific scope  — see `LEAVE_PLAN_SCOPE_SPECIFICITY`
 *   2. higher `priority`
 *   3. later `validFrom`
 *   4. smaller assignment id (so the answer is never random)
 * Only assignments to `active` plans take part. Plans are never merged: the winner's rule for the
 * leave type is used whole, field by field falling back to the leave type, and a winning plan with
 * no rule for the leave type falls back to the leave type — not to a lower-ranked plan.
 *
 * RN-SAFE: zod only, no Node built-ins.
 */

import { z } from 'zod';
import { EMPLOYMENT_TYPES } from './enums.js';
import {
  LEAVE_RULE_EXTENSION_NEUTRAL,
  leaveAccrualBasisSchema,
  leaveEventTypeSchema,
  leaveQuotaModeSchema,
  leaveRuleExtensionBodyShape,
  leaveRuleExtensionSchema,
  leaveSandwichModeSchema,
  refineLeaveRuleExtension,
} from './leave-policy-rules.js';
import {
  accrualFrequencySchema,
  dateOnlySchema,
  instantSchema,
  leaveDaysSchema,
  orgIdSchema,
  uuidSchema,
} from './schemas/common.js';

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

/** `draft`: being built, takes part in nothing. `active`: its assignments resolve. `archived`:
 * retired — kept, with its history, but its assignments no longer resolve. */
export const LEAVE_PLAN_STATUSES = ['draft', 'active', 'archived'] as const;
export const leavePlanStatusSchema = z.enum(LEAVE_PLAN_STATUSES);
export type LeavePlanStatus = z.infer<typeof leavePlanStatusSchema>;

export const leavePlanSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  status: leavePlanStatusSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type LeavePlan = z.infer<typeof leavePlanSchema>;

/** A plan as the list shows it: how much it carries, and how many people it currently reaches
 * is deliberately not here — that is a per-person resolution, answered by the explain route. */
export const leavePlanListItemSchema = leavePlanSchema.extend({
  /** Leave types with an open rule in this plan. */
  openRuleCount: z.number().int().nonnegative(),
  /** Assignments in force today. */
  openAssignmentCount: z.number().int().nonnegative(),
});
export type LeavePlanListItem = z.infer<typeof leavePlanListItemSchema>;

export const listLeavePlansQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  status: leavePlanStatusSchema.optional(),
  /** Matches the name, case-insensitively. */
  search: z.string().trim().max(100).optional(),
});

export const createLeavePlanBodySchema = z
  .object({
    /** Unique within the organisation, case-insensitively. */
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).nullable().default(null),
    status: leavePlanStatusSchema.default('draft'),
  })
  .strict();
export type CreateLeavePlanBody = z.infer<typeof createLeavePlanBodySchema>;

export const updateLeavePlanBodySchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    status: leavePlanStatusSchema.optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to change');
export type UpdateLeavePlanBody = z.infer<typeof updateLeavePlanBodySchema>;

// ---------------------------------------------------------------------------
// Rules (effective-dated, one open row per plan and leave type)
// ---------------------------------------------------------------------------

/** A quota or cap as a decimal string; `"0"` is a real value (no days), unlike on `leaveDaysSchema`. */
const ruleDaysSchema = leaveDaysSchema.or(z.literal('0'));

/**
 * The fields a plan rule may override. NULL MEANS INHERIT the leave type's value; it never means
 * "unlimited" or "none". The probation and notice rules are new behaviour with a neutral default:
 *   - `accrueDuringProbation`: false stops accrual while the person is on probation. The default
 *     (true) accrues exactly as today.
 *   - `accrualStartsAfterDays`: accrual begins only this many days after joining; null = from day one.
 *   - `maxDaysPerRequestInNotice`: the most days one request may take once the person is serving
 *     notice; null = no extra limit. `"0"` means no leave may be requested during notice.
 */
export const leavePlanRuleValuesSchema = leaveRuleExtensionSchema.extend({
  annualQuota: ruleDaysSchema.nullable(),
  accrualFrequency: accrualFrequencySchema.nullable(),
  carryForwardCap: ruleDaysSchema.nullable(),
  accrueDuringProbation: z.boolean(),
  accrualStartsAfterDays: z.number().int().min(0).max(1095).nullable(),
  maxDaysPerRequestInNotice: ruleDaysSchema.nullable(),
});
export type LeavePlanRuleValues = z.infer<typeof leavePlanRuleValuesSchema>;

/** What an untouched rule field means — the neutral values that reproduce today's behaviour. */
export const LEAVE_PLAN_RULE_NEUTRAL: LeavePlanRuleValues = {
  annualQuota: null,
  accrualFrequency: null,
  carryForwardCap: null,
  accrueDuringProbation: true,
  accrualStartsAfterDays: null,
  maxDaysPerRequestInNotice: null,
  ...LEAVE_RULE_EXTENSION_NEUTRAL,
};

export const leavePlanRuleSchema = leavePlanRuleValuesSchema.extend({
  id: uuidSchema,
  orgId: orgIdSchema,
  planId: uuidSchema,
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  validFrom: dateOnlySchema,
  /** Null on the open row. */
  validTo: dateOnlySchema.nullable(),
  createdAt: instantSchema,
});
export type LeavePlanRule = z.infer<typeof leavePlanRuleSchema>;

export const setLeavePlanRuleBodySchema = z
  .object({
    annualQuota: ruleDaysSchema.nullable().default(null),
    accrualFrequency: accrualFrequencySchema.nullable().default(null),
    carryForwardCap: ruleDaysSchema.nullable().default(null),
    accrueDuringProbation: z.boolean().default(true),
    accrualStartsAfterDays: z.number().int().min(0).max(1095).nullable().default(null),
    maxDaysPerRequestInNotice: ruleDaysSchema.nullable().default(null),
    /** Phase 4c (leave-policy-rules.ts): advance leave, carry-forward expiry, sandwich, request
     * limits, combination limits, accrual basis, special leave. Absent = null = inherit. */
    ...leaveRuleExtensionBodyShape,
    /** When this version takes effect. The open row closes the day before; a date at or before the
     * open row's own `validFrom` is refused — history is never rewritten. */
    validFrom: dateOnlySchema,
  })
  .strict()
  .superRefine(refineLeaveRuleExtension);
export type SetLeavePlanRuleBody = z.infer<typeof setLeavePlanRuleBodySchema>;

// ---------------------------------------------------------------------------
// Assignments (who a plan applies to)
// ---------------------------------------------------------------------------

/**
 * What an assignment is aimed at. `department`, `location` and `grade` name a row by id
 * (`scopeValueId`); `employment_type` names one of `EMPLOYMENT_TYPES` (`scopeValue`);
 * `everyone` names nothing and is the catch-all.
 */
export const LEAVE_PLAN_SCOPES = [
  'department',
  'location',
  'grade',
  'employment_type',
  'everyone',
] as const;
export const leavePlanScopeSchema = z.enum(LEAVE_PLAN_SCOPES);
export type LeavePlanScope = z.infer<typeof leavePlanScopeSchema>;

/** Higher wins. A person has exactly one department, location, grade and employment type at a
 * time, so scopes compete by this order and `priority` breaks ties between plans aimed at the
 * same scope. The order is a product decision (ADR 0032, D3), not an accident of enum order. */
export const LEAVE_PLAN_SCOPE_SPECIFICITY: Readonly<Record<LeavePlanScope, number>> = {
  department: 4,
  location: 3,
  grade: 2,
  employment_type: 1,
  everyone: 0,
};

/** True when the scope is aimed at a catalogue row by id (`scopeValueId`). */
export function leavePlanScopeUsesId(scope: LeavePlanScope): boolean {
  return scope === 'department' || scope === 'location' || scope === 'grade';
}

/** The shape rule shared by the request body and the database CHECK: exactly the value the scope
 * needs, and nothing else. Returns the message of the first violation, or null. */
export function leavePlanScopeValueProblem(
  scope: LeavePlanScope,
  scopeValueId: string | null,
  scopeValue: string | null,
): string | null {
  if (leavePlanScopeUsesId(scope)) {
    if (scopeValueId === null) return 'This scope needs the id of the department, location or grade';
    if (scopeValue !== null) return 'Only the employment_type scope takes a scopeValue';
    return null;
  }
  if (scope === 'employment_type') {
    if (scopeValue === null || !(EMPLOYMENT_TYPES as readonly string[]).includes(scopeValue)) {
      return 'The employment_type scope needs a scopeValue that is an employment type';
    }
    if (scopeValueId !== null) return 'The employment_type scope takes no scopeValueId';
    return null;
  }
  if (scopeValueId !== null || scopeValue !== null) return 'The everyone scope takes no value';
  return null;
}

export const LEAVE_PLAN_PRIORITY_MAX = 1000;

export const leavePlanAssignmentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  planId: uuidSchema,
  planName: z.string(),
  scope: leavePlanScopeSchema,
  scopeValueId: uuidSchema.nullable(),
  scopeValue: z.string().nullable(),
  /** The department, location or grade name, or the employment type, for display; null for everyone. */
  scopeLabel: z.string().nullable(),
  priority: z.number().int().min(0).max(LEAVE_PLAN_PRIORITY_MAX),
  validFrom: dateOnlySchema,
  /** Null on the open row. */
  validTo: dateOnlySchema.nullable(),
  createdAt: instantSchema,
});
export type LeavePlanAssignment = z.infer<typeof leavePlanAssignmentSchema>;

export const listLeavePlanAssignmentsQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  planId: uuidSchema.optional(),
  scope: leavePlanScopeSchema.optional(),
  /** The date to list assignments in force on; absent = today in the organisation's terms. */
  asOf: dateOnlySchema.optional(),
  /** True lists every row, closed ones included, and ignores `asOf`. */
  history: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

/**
 * Aim a plan at a scope from a date, or stop doing so. The row for the same plan and target that
 * is open is closed the day before `validFrom`; with `active: true` a new row opens from
 * `validFrom` (a changed priority is a new row, like any other change), with `active: false`
 * nothing opens and the plan simply stops applying to that target.
 */
export const setLeavePlanAssignmentBodySchema = z
  .object({
    planId: uuidSchema,
    scope: leavePlanScopeSchema,
    scopeValueId: uuidSchema.nullable().default(null),
    scopeValue: z.string().trim().min(1).max(40).nullable().default(null),
    priority: z.number().int().min(0).max(LEAVE_PLAN_PRIORITY_MAX).default(0),
    validFrom: dateOnlySchema,
    active: z.boolean().default(true),
  })
  .strict()
  .superRefine((value, ctx) => {
    const problem = leavePlanScopeValueProblem(value.scope, value.scopeValueId, value.scopeValue);
    if (problem) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [value.scope === 'employment_type' ? 'scopeValue' : 'scopeValueId'],
        message: problem,
      });
    }
  });
export type SetLeavePlanAssignmentBody = z.infer<typeof setLeavePlanAssignmentBodySchema>;

/** The result of aiming a plan: the row that opened, or null when the call only ended one. */
export const setLeavePlanAssignmentResultSchema = z.object({
  assignment: leavePlanAssignmentSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Plan detail
// ---------------------------------------------------------------------------

export const getLeavePlanQuerySchema = z.object({
  /** The date to show rules and assignments in force on; absent = today in the organisation's terms. */
  asOf: dateOnlySchema.optional(),
  /** True lists every rule and assignment row, closed ones included, and ignores `asOf`. */
  history: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

export const leavePlanDetailSchema = z.object({
  plan: leavePlanSchema,
  rules: z.array(leavePlanRuleSchema),
  assignments: z.array(leavePlanAssignmentSchema),
});
export type LeavePlanDetail = z.infer<typeof leavePlanDetailSchema>;

// ---------------------------------------------------------------------------
// Explain: which plan and rule apply to one person and leave type on a date
// ---------------------------------------------------------------------------

export const leavePlanValueSourceSchema = z.enum(['plan_rule', 'leave_type']);
export type LeavePlanValueSource = z.infer<typeof leavePlanValueSourceSchema>;

/** A resolved value and where it came from. `value` is null when the source itself holds none
 * (a leave type with no cap). */
function sourced<T extends z.ZodTypeAny>(value: T) {
  return z.object({ value: value.nullable(), source: leavePlanValueSourceSchema });
}

/** The values that will be used. Probation and notice rules have no leave-type counterpart; their
 * `leave_type` source means the neutral default (accrue, no waiting, no notice limit). */
export const leavePlanEffectiveValuesSchema = z.object({
  annualQuota: sourced(z.string()),
  accrualFrequency: sourced(accrualFrequencySchema),
  carryForwardCap: sourced(z.string()),
  accrueDuringProbation: z.object({ value: z.boolean(), source: leavePlanValueSourceSchema }),
  accrualStartsAfterDays: sourced(z.number().int()),
  maxDaysPerRequestInNotice: sourced(z.string()),
  // Phase 4c. A `leave_type` source with a null value means the field is not set there either, so
  // today's behaviour applies.
  negativeBalanceMaxDays: sourced(z.string()),
  carryForwardExpiryDays: sourced(z.number().int()),
  carryForwardExpiryMonthDay: sourced(z.string()),
  sandwichMode: sourced(leaveSandwichModeSchema),
  sandwichMinDays: sourced(z.number().int()),
  minDaysPerRequest: sourced(z.string()),
  maxDaysPerRequest: sourced(z.string()),
  maxRequestsPerMonth: sourced(z.number().int()),
  maxRequestsPerYear: sourced(z.number().int()),
  notCombinableWithLeaveTypeIds: sourced(z.array(uuidSchema)),
  accrualBasis: sourced(leaveAccrualBasisSchema),
  prorateLeavers: sourced(z.boolean()),
  accrualPerPayPeriod: sourced(z.boolean()),
  leaveEventType: sourced(leaveEventTypeSchema),
  childCountLimit: sourced(z.number().int()),
  quotaMode: sourced(leaveQuotaModeSchema),
  eventQuotaDays: sourced(z.string()),
});
export type LeavePlanEffectiveValues = z.infer<typeof leavePlanEffectiveValuesSchema>;

/** Why a considered assignment did or did not win. */
export const LEAVE_PLAN_CANDIDATE_OUTCOMES = [
  'selected',
  'outranked_by_scope',
  'outranked_by_priority',
  'outranked_by_start',
  'outranked_by_id',
  'plan_not_active',
  'does_not_apply',
] as const;
export const leavePlanCandidateOutcomeSchema = z.enum(LEAVE_PLAN_CANDIDATE_OUTCOMES);
export type LeavePlanCandidateOutcome = z.infer<typeof leavePlanCandidateOutcomeSchema>;

/** What the person was matched against on the date — the facts behind every `does_not_apply`. */
export const leavePlanSubjectSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  employmentId: uuidSchema,
  departmentId: uuidSchema.nullable(),
  locationId: uuidSchema.nullable(),
  gradeId: uuidSchema.nullable(),
  employmentType: z.string(),
  /** On probation on the date (joined, `probationEndOn` not yet passed, not confirmed). */
  inProbation: z.boolean(),
  /** Serving notice on the date. */
  inNotice: z.boolean(),
});
export type LeavePlanSubject = z.infer<typeof leavePlanSubjectSchema>;

export const leavePlanCandidateSchema = z.object({
  assignment: leavePlanAssignmentSchema,
  outcome: leavePlanCandidateOutcomeSchema,
  /** One human sentence, e.g. "Department outranks location". */
  reason: z.string(),
});

/**
 * `plan_rule`: a plan's rule applied. `no_assignment`: no active plan reaches this person on the
 * date. `no_rule`: a plan reaches them but has no rule for this leave type. In the last two the
 * leave type's own values apply, unchanged.
 */
export const leavePlanResolutionSchema = z.enum(['plan_rule', 'no_assignment', 'no_rule']);
export type LeavePlanResolution = z.infer<typeof leavePlanResolutionSchema>;

export const explainLeavePlanQuerySchema = z.object({
  personId: uuidSchema,
  leaveTypeId: uuidSchema,
  /** The date to resolve on, in the person's own location terms; absent = today there. */
  asOf: dateOnlySchema.optional(),
});

export const leavePlanExplanationSchema = z.object({
  asOf: dateOnlySchema,
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  subject: leavePlanSubjectSchema,
  resolution: leavePlanResolutionSchema,
  /** The winning plan; null unless `resolution` is `plan_rule` or `no_rule`. */
  plan: leavePlanSchema.nullable(),
  assignment: leavePlanAssignmentSchema.nullable(),
  /** The rule row in force on `asOf`; null unless `resolution` is `plan_rule`. */
  rule: leavePlanRuleSchema.nullable(),
  effective: leavePlanEffectiveValuesSchema,
  /** Every assignment in force on the date, ranked, so the answer can be checked by eye. */
  considered: z.array(leavePlanCandidateSchema),
});
export type LeavePlanExplanation = z.infer<typeof leavePlanExplanationSchema>;
